import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import type { ProductMasterListItem } from '@/types/product-master';

function PackageIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

/** "Stores n / N": teal when every store sells it, amber when some do, grey when none. */
export function StoresPill({
  count,
  total,
  onClick,
}: {
  count: number;
  total: number;
  onClick?: () => void;
}) {
  const tone =
    count === 0
      ? 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'
      : total > 0 && count >= total
        ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  const classes = cn(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums transition-all duration-200',
    tone,
  );
  const text = `Stores ${count} / ${total}`;
  if (!onClick) return <span className={classes}>{text}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(classes, 'hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500')}
      aria-label={`${text}: choose stores`}
    >
      {text}
    </button>
  );
}

/** Columns for a table of masters: one row per product, whatever store sells it. */
export function makeMasterColumns(opts: {
  currency: CurrencyCode;
  storeTotal: number;
  onOpen: (row: ProductMasterListItem) => void;
  onStores?: (row: ProductMasterListItem) => void;
}): ColumnDef<ProductMasterListItem>[] {
  const { currency, storeTotal, onOpen, onStores } = opts;
  return [
    {
      key: 'name',
      header: 'Product',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          {row.image_url ? (
            <img src={row.image_url} alt={row.name} className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-100 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/20">
              <PackageIcon className="h-5 w-5 text-teal-500 dark:text-teal-400" />
            </div>
          )}
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => onOpen(row)}
              className="truncate rounded text-left text-sm font-medium text-neutral-900 hover:text-teal-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-neutral-100 dark:hover:text-teal-400"
            >
              {row.name}
            </button>
            <p className="text-xs tabular-nums text-neutral-400 dark:text-neutral-500">{row.sku}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-300">{row.category ?? '—'}</span>
      ),
    },
    {
      key: 'is_variant',
      header: 'Type',
      render: (_v, row) =>
        row.is_variant ? (
          <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
            Variant
          </span>
        ) : (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {row.product_type === 'SERVICE' ? 'Service' : 'Product'}
          </span>
        ),
    },
    {
      key: 'default_price',
      header: 'Price',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatPaise(row.default_price, currency)}
        </span>
      ),
    },
    {
      key: 'enabled_store_count',
      header: 'Stores',
      render: (_v, row) => (
        <StoresPill
          count={row.enabled_store_count}
          total={storeTotal}
          onClick={onStores ? () => onStores(row) : undefined}
        />
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_v, row) =>
        row.is_draft ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            Draft
          </span>
        ) : (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
              row.is_active
                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
            )}
          >
            <span className={cn('h-2 w-2 rounded-full', row.is_active ? 'bg-success-500' : 'bg-neutral-400')} />
            {row.is_active ? 'Active' : 'Inactive'}
          </span>
        ),
    },
  ];
}
